'use client';

import { Box, Button, Grid, Group } from '@mantine/core';
import { SectionContentProps } from '../types';
import { useGetActivitiesQuery } from '@app/types/blackroom-api';
import { useTranslation } from 'react-i18next';
import User from '@app/shared/ui-kit/atoms/User';
import * as classes from './styles/index.css';
import { SectionActivityColumn } from './types';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

const PAGE_SIZE = 15;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function SectionActivities({ resource }: SectionContentProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation('Activity');
  const workspace = useWorkspace();

  const activities = useGetActivitiesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: { workspaceId: workspace.id, targetId: resource.id },
      limit: PAGE_SIZE,
      offset: 0,
      workspaceId: workspace.id,
    },
  });

  const nodes = activities.data?.activities.nodes;
  if (!nodes) {
    return <Box></Box>;
  }

  const totalCount = activities.data?.activities.pageInfo?.totalCount ?? 0;
  const hasMore = nodes.length < totalCount;

  const handleShowMore = async () => {
    await activities.fetchMore({
      variables: {
        filter: { workspaceId: workspace.id, targetId: resource.id },
        limit: PAGE_SIZE,
        offset: nodes.length,
        workspaceId: workspace.id,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return {
          ...previousResult,
          activities: {
            ...fetchMoreResult.activities,
            nodes: [
              ...(previousResult.activities.nodes ?? []),
              ...(fetchMoreResult.activities.nodes ?? []),
            ],
          },
        };
      },
    });
  };

  const columns: {
    key: SectionActivityColumn;
    span: Parameters<typeof Grid.Col>['0']['span'];
    render: (
      value: NonNullable<typeof nodes>[number],
    ) => string | React.ReactNode;
  }[] = [
    {
      key: 'user',
      span: 'content',
      render: ({ user }) => <User key={user.email} size={24} user={user} />,
    },
    {
      key: 'topic',
      span: 'auto',
      render: ({ topic }) => t(`ActivityTopic.${topic}`),
    },
    {
      key: 'createdAt',
      span: 'content',
      render: ({ createdAt }) =>
        new Intl.DateTimeFormat(language, {
          dateStyle: 'short',
          timeStyle: 'medium',
        }).format(new Date(createdAt)),
    },
  ];

  /* ******************************** RENDERING ******************************* */

  return (
    <Box>
      <Grid
        className={classes.activityGrid}
        styles={{ inner: { margin: 0, width: '100%' } }}
        px="sm"
      >
        {columns.map((column) => (
          <Grid.Col
            key={column.key}
            span={column.span}
            className={classes.activityColumn}
          >
            {nodes.map((node) => (
              <Group key={node.id} className={classes.activityCell[column.key]}>
                {column.render(node)}
              </Group>
            ))}
          </Grid.Col>
        ))}
      </Grid>
      {hasMore && (
        <Box px="sm" pb="xs">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            fullWidth
            loading={activities.loading}
            onClick={handleShowMore}
          >
            {t('SectionActivities.showMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
